import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Target, Activity } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { NextPageLink } from '../components/NextPageLink';
import { useSEO } from '../utils/seo';
export const TechnologieLaser = () => {
  useSEO({
    title: 'Technologie Laser Picoseconde & Q-Switched | Détatouage Avancé | Dispel Your Tattoo',
    description: 'Découvrez les technologies laser picoseconde et Q-Switched utilisées chez Dispel Your Tattoo à Martigny. Sécurité, précision et expertise pour un détatouage optimal en Valais.',
    canonical: 'https://www.dispelyourtattoo.ch/technologie-laser'
  });
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <main className="pt-32 pb-0 bg-brand-dark" data-id="element-1107">
      {/* Anchor Navigation */}
      <div className="sticky top-[72px] md:top-[88px] z-40 bg-brand-dark/95 backdrop-blur-md border-b border-brand-border/50 py-3 md:py-4" data-id="element-1108">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex justify-center space-x-4 md:space-x-8" data-id="element-1109">
          <a href="#technologie" onClick={e => scrollToSection(e, 'technologie')} className="text-[10px] md:text-sm font-medium text-brand-gray hover:text-brand-light transition-colors uppercase tracking-wider" data-id="element-1110">
            Technologie
          </a>
          <a href="#securite" onClick={e => scrollToSection(e, 'securite')} className="text-[10px] md:text-sm font-medium text-brand-gray hover:text-brand-light transition-colors uppercase tracking-wider" data-id="element-1111">
            Sécurité
          </a>
          <a href="#expertise" onClick={e => scrollToSection(e, 'expertise')} className="text-[10px] md:text-sm font-medium text-brand-gray hover:text-brand-light transition-colors uppercase tracking-wider" data-id="element-1112">
            Expertise
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 lg:py-40 overflow-hidden" data-id="element-1113">
        <div className="absolute inset-0 z-0" data-id="element-1114">
          <img src="https://static.wixstatic.com/media/5df06d_6f561572027f408abe73c326ee33d45b~mv2.jpg" alt="Détatouage laser en cours" className="w-full h-full object-cover opacity-30 grayscale" data-id="element-1115" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-darker/80 via-brand-darker/60 to-brand-darker/90" data-id="element-1116" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 text-center" data-id="element-1117">
          <ScrollReveal data-id="element-1118">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-medium text-brand-light mb-6 md:mb-8 tracking-tight leading-[1.2] text-balance" data-id="element-1119">
              Détatouage Laser
              <br data-id="element-1120" />
              Picoseconde
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.1} data-id="element-1121">
            <p className="text-lg md:text-xl lg:text-2xl text-brand-gray font-light leading-relaxed max-w-3xl mx-auto mb-10 md:mb-12" data-id="element-1122">
              L'excellence technologique au service de votre peau. Une approche
              précise, sécurisée et sur-mesure pour effacer vos tatouages.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.2} data-id="element-1123">
            <Link to="/contact#consultation-form" className="inline-flex items-center space-x-2 px-8 md:px-10 py-4 md:py-5 bg-brand-light text-brand-dark rounded-full font-bold uppercase tracking-wider hover:bg-white transition-colors duration-300 text-sm md:text-base" data-id="element-1124">
              <span data-id="element-1125">Prendre rendez-vous</span>
              <ArrowRight size={16} className="md:w-[18px] md:h-[18px]" data-id="element-1126" />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technologie" className="py-16 md:py-24 lg:py-32 bg-brand-darker scroll-mt-24" data-id="element-1127">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12" data-id="element-1128">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1129">
            <div className="order-2 lg:order-1" data-id="element-1130">
              <ScrollReveal data-id="element-1131">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-brand-light mb-6 md:mb-8 tracking-tight" data-id="element-1132">
                  La puissance combinée du Picoseconde et Q-Switched
                </h2>
              </ScrollReveal>
              <ScrollReveal delay={0.1} data-id="element-1133">
                <div className="space-y-4 md:space-y-6 text-base md:text-lg text-brand-gray font-light leading-relaxed" data-id="element-1134">
                  <p data-id="element-1135">
                    En matière de détatouage laser, la technologie fait toute la
                    différence. Chez Dispel Your Tattoo, j'utilise les deux
                    technologies les plus reconnues du domaine, afin d'optimiser
                    chaque traitement selon le type d'encre, sa profondeur et la
                    complexité du tatouage.
                  </p>
                  <ul className="space-y-4 mt-6 md:mt-8" data-id="element-1136">
                    <li className="flex items-start space-x-3 md:space-x-4" data-id="element-1137">
                      <Target className="w-5 h-5 md:w-6 md:h-6 text-brand-light shrink-0 mt-1" data-id="element-1138" />
                      <span data-id="element-1139">
                        <strong data-id="element-1140">Laser Picoseconde :</strong> Génère des
                        impulsions ultra-courtes, idéales pour fragmenter les
                        encres résistantes en particules minuscules, facilement
                        éliminées par l'organisme.
                      </span>
                    </li>
                    <li className="flex items-start space-x-3 md:space-x-4" data-id="element-1141">
                      <Activity className="w-5 h-5 md:w-6 md:h-6 text-brand-light shrink-0 mt-1" data-id="element-1142" />
                      <span data-id="element-1143">
                        <strong data-id="element-1144">Laser Q-Switched :</strong> Très efficace pour
                        les pigments noirs et foncés plus profonds, offrant une
                        base solide pour le traitement.
                      </span>
                    </li>
                  </ul>
                  <p className="pt-2 md:pt-4" data-id="element-1145">
                    La combinaison des deux permet d'adapter précisément chaque
                    séance pour un résultat optimal.
                  </p>
                </div>
              </ScrollReveal>
            </div>
            <div className="order-1 lg:order-2 relative h-[300px] md:h-[500px] lg:h-[700px] rounded-2xl md:rounded-3xl overflow-hidden" data-id="element-1146">
              <ScrollReveal className="w-full h-full" clipReveal="left" data-id="element-1147">
                <img src="https://cdn.magicpatterns.com/uploads/gPuGLFxqLPD9w6gA9pCBvR/IMG_2731-PROOF-THOMAS-MASOTTI.jpg" alt="Technologie Laser" className="w-full h-full object-cover grayscale-[20%]" data-id="element-1148" />
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Process Section */}
      <section id="securite" className="py-16 md:py-24 lg:py-32 bg-brand-dark scroll-mt-24" data-id="element-1149">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12" data-id="element-1150">
          <div className="max-w-4xl mx-auto text-center mb-12 md:mb-20" data-id="element-1151">
            <ScrollReveal data-id="element-1152">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium text-brand-light mb-4 md:mb-6 tracking-tight" data-id="element-1153">
                Sécurité et Processus
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1} data-id="element-1154">
              <p className="text-lg md:text-xl text-brand-gray font-light leading-relaxed" data-id="element-1155">
                Cette complémentarité technologique garantit un traitement
                performant, sécurisé et conforme aux standards professionnels
                les plus récents.
              </p>
            </ScrollReveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-1156">
            {[{
            title: 'Évaluation sur-mesure',
            desc: "Chaque tatouage est unique. Nous analysons l'encre, la profondeur et votre type de peau pour définir le protocole idéal.",
            icon: <Target className="w-6 h-6 md:w-8 md:h-8 text-brand-light" data-id="element-1157" />
          }, {
            title: 'Protection de la peau',
            desc: "Les impulsions ultra-courtes ciblent uniquement l'encre, préservant les tissus environnants pour une cicatrisation optimale.",
            icon: <ShieldCheck className="w-6 h-6 md:w-8 md:h-8 text-brand-light" data-id="element-1158" />
          }, {
            title: 'Suivi personnalisé',
            desc: "Un accompagnement complet entre chaque séance pour s'assurer de la bonne élimination des pigments par votre système immunitaire.",
            icon: <Activity className="w-6 h-6 md:w-8 md:h-8 text-brand-light" data-id="element-1159" />
          }].map((item, i) => <ScrollReveal key={i} delay={i * 0.1} className="h-full [&>div]:h-full" data-id="element-1160">
                <div className="bg-brand-darker border border-brand-border/50 rounded-2xl p-6 md:p-10 h-full hover:border-brand-light/30 transition-colors" data-id="element-1161">
                  <div className="mb-4 md:mb-6" data-id="element-1162">{item.icon}</div>
                  <h3 className="text-xl md:text-2xl font-bold text-brand-light mb-3 md:mb-4" data-id="element-1163">
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base text-brand-gray font-light leading-relaxed" data-id="element-1164">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Expertise / Brand Story Section */}
      <section id="expertise" className="py-20 md:py-32 lg:py-40 bg-brand-darker scroll-mt-24" data-id="element-1165">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12" data-id="element-1166">
          <div className="max-w-3xl" data-id="element-1167">
            <ScrollReveal data-id="element-1168">
              <motion.div initial={{
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 1,
              ease: [0.16, 1, 0.3, 1]
            }} className="h-[1px] w-16 md:w-24 bg-brand-border mb-8 md:mb-12 origin-left" data-id="element-1169" />
            </ScrollReveal>

            <ScrollReveal delay={0.1} data-id="element-1170">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-brand-light mb-6 md:mb-10 tracking-tight text-balance" data-id="element-1171">
                Une expertise spécialisée
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1172">
              <div className="max-w-prose space-y-5 text-base md:text-lg lg:text-xl text-brand-gray font-light leading-relaxed" data-id="element-1173">
                <p data-id="element-1174">
                  Bienvenue chez Dispel Your Tattoo, un espace entièrement dédié
                  au détatouage, situé au sein du salon Pleine Lune Tattoo à
                  Martigny, en Valais.
                </p>
                <p data-id="element-1175">
                  Mon travail est exclusivement concentré sur l'effacement de
                  tatouages, ce qui me permet de proposer une expertise
                  spécialisée, précise et constamment actualisée.
                </p>
                <p data-id="element-1176">
                  Depuis maintenant quatre ans, j'accompagne mes clients vers un
                  résultat sûr, maîtrisé et adapté à chaque type de peau. Chaque
                  projet est traité avec sérieux, transparence et respect de
                  votre peau.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-brand-light text-brand-dark text-center" data-id="element-1177">
        <div className="max-w-[800px] mx-auto px-6 md:px-12" data-id="element-1178">
          <ScrollReveal data-id="element-1179">
            <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-6 md:mb-8 tracking-tighter" data-id="element-1180">
              Prêt à commencer ?
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.1} data-id="element-1181">
            <p className="text-lg md:text-xl font-medium text-brand-dark/70 mb-8 md:mb-12" data-id="element-1182">
              Prenez rendez-vous pour une consultation personnalisée et
              découvrez comment notre technologie peut vous aider.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.2} data-id="element-1183">
            <Link to="/contact#consultation-form" className="inline-flex items-center space-x-2 px-8 md:px-10 py-4 md:py-5 bg-brand-dark text-brand-light rounded-full font-bold uppercase tracking-wider hover:bg-brand-darker transition-colors duration-300 text-sm md:text-base" data-id="element-1184">
              <span data-id="element-1185">Réserver une consultation</span>
              <ArrowRight size={16} className="md:w-[18px] md:h-[18px]" data-id="element-1186" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
      <NextPageLink data-id="element-1187" />
    </main>;
};